import React, { useState } from "react";
import { Opportunity } from "@/opportunity/opportunity";
import { useStatusDetails } from "@/opportunity/useOpportunityStatuses";
import useOpportunityStatuses from "@/opportunity/useOpportunityStatuses";
import { Button } from "@/components/ui/button";
import { StatusCard } from "./StatusCard";
import { useStatusCardAnimation } from "./hooks/useStatusCardAnimation";
import { useStatusChange } from "./hooks/useStatusChange";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight } from "lucide-react";

interface RecommendedStatusesDialogProps {
  opportunity: Opportunity;
}

const RecommendedStatusesDialog: React.FC<RecommendedStatusesDialogProps> = ({
  opportunity
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { selectedStatus, isExpanding, animationDuration, selectStatus, clearSelection } = useStatusCardAnimation();
  const {
    isProcessing,
    isError,
    changeStatus
  } = useStatusChange(opportunity);

  if (!opportunity?.status) {
    return null;
  }

  const currentStatusDetails = useStatusDetails(opportunity.status);
  const { data: allStatuses } = useOpportunityStatuses();

  const recommendedStatuses = currentStatusDetails.recommended_next_statuses || [];
  
  // Find "Closed (not shared)" status and add it as "No Issue Found" option
  const closedNotSharedStatus = allStatuses?.find((status) => 
    status.name.toLowerCase().includes('closed') && 
    status.name.toLowerCase().includes('not shared')
  );
  
  // Create a "No Issue Found" option that maps to "Closed (not shared)"
  const noIssueFoundOption = closedNotSharedStatus ? {
    name: closedNotSharedStatus.name,
    action_verb: 'No Issue Found',
    action_description: closedNotSharedStatus.description_customer_facing || closedNotSharedStatus.description_internal,
    lucide_icon_id: closedNotSharedStatus.lucide_icon_id || 'check',
    requires_reason: closedNotSharedStatus.requires_reason || false,
    available_reasons: closedNotSharedStatus.available_reasons || []
  } : null;

  // Combine recommended statuses with "No Issue Found" option
  const allRecommendedStatuses = [
    ...recommendedStatuses,
    ...(noIssueFoundOption ? [noIssueFoundOption] : [])
  ];

  if (allRecommendedStatuses.length === 0) {
    return null;
  }

  const renderStatusCards = () => (
    <div className="flex flex-col gap-3 overflow-hidden">
      {allRecommendedStatuses.map((recommendedStatus) => {
        const isExpanded = selectedStatus === recommendedStatus.name;
        const hasOtherSelection = selectedStatus !== null && !isExpanded;
        const isCurrentlyProcessing = isProcessing === recommendedStatus.name;

        return (
          <StatusCard
            key={recommendedStatus.name}
            statusName={recommendedStatus.name}
            statusDetails={recommendedStatus}
            isExpanded={isExpanded}
            isExpanding={isExpanding}
            isProcessing={isCurrentlyProcessing}
            hasOtherSelection={hasOtherSelection}
            animationDuration={animationDuration}
            onSelect={() => selectStatus(recommendedStatus.name)}
            onCancel={clearSelection}
            onSubmit={(comments, reason) => handleStatusChange(recommendedStatus.name, comments, reason)}
          />
        );
      })}
    </div>
  );

  const handleStatusChange = async (statusName: string, comments: string, reason?: string) => {
    try {
      await changeStatus(statusName, comments, reason);
      clearSelection();
      setDialogOpen(false);
    } catch (error) {
      // Error is already logged in the hook
    }
  };

  return (
    <>
      <Button
        variant="default"
        size="sm"
        onClick={() => setDialogOpen(true)}
        className="flex items-center gap-1.5 sm:gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium"
      >
        <ArrowRight size={16} />
        <span className="sm:hidden">Status</span>
        <span className="hidden sm:inline">Change Status</span>
      </Button>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl w-[90vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Recommended Next Steps</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col space-y-3 pt-2 overflow-y-auto">
            {renderStatusCards()}
            {isError && (
              <p className="text-sm text-red-600 mt-2">
                Failed to change status. Please try again or use the full status change form.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecommendedStatusesDialog;